/**
 * Dev-only validation harness for tick-size resolution diagnostics.
 * Use in browser console to validate representative symbols across asset classes.
 *
 * Example:
 *   const v = window.__tickSizeValidator
 *   v.validateAllSymbols()
 *   v.validateSymbol('ESZ25')
 *   v.validateSymbol('CLZ25')
 */

import {
  getTickSizeResolutionDiagnostic,
  TickSizeResolutionDiagnostic,
  CME_FUTURES_TICK_SIZES,
  extractFuturesRoot,
} from '../constants/contractMultipliers';

/**
 * Representative sample symbols covering all asset classes and supported/unsupported cases.
 * Format: { symbol, assetClass, description, expectedSource, expectedTickSize? }
 */
export const VALIDATION_SAMPLE_SET = [
  // === INDEX FUTURES (Equity Index) ===
  {
    symbol: 'ESZ25',
    assetClass: 'FUTURES',
    description: 'S&P 500 E-mini Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.25,
  },
  {
    symbol: 'MESZ25',
    assetClass: 'FUTURES',
    description: 'S&P 500 Micro E-mini Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.25,
  },
  {
    symbol: 'NQZ25',
    assetClass: 'FUTURES',
    description: 'Nasdaq 100 E-mini Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.25,
  },
  {
    symbol: 'MNQZ25',
    assetClass: 'FUTURES',
    description: 'Nasdaq 100 Micro E-mini Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.25,
  },
  {
    symbol: 'YMZ25',
    assetClass: 'FUTURES',
    description: 'Mini Dow Jones Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 1,
  },
  {
    symbol: 'MYMZ25',
    assetClass: 'FUTURES',
    description: 'Micro Dow Jones Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 1,
  },
  {
    symbol: 'M2KZ25',
    assetClass: 'FUTURES',
    description: 'Micro Russell 2000 Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.1,
  },

  // === ENERGY ===
  {
    symbol: 'CLF26',
    assetClass: 'FUTURES',
    description: 'WTI Crude Oil Feb2026',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.01,
  },
  {
    symbol: 'MCLF26',
    assetClass: 'FUTURES',
    description: 'Micro WTI Crude Oil Feb2026',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.01,
  },
  {
    symbol: 'NGF26',
    assetClass: 'FUTURES',
    description: 'Natural Gas Feb2026',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.001,
  },

  // === METALS ===
  {
    symbol: 'GCZ25',
    assetClass: 'FUTURES',
    description: 'Gold Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.1,
  },
  {
    symbol: 'MGCZ25',
    assetClass: 'FUTURES',
    description: 'Micro Gold Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.1,
  },
  {
    symbol: 'SIZ25',
    assetClass: 'FUTURES',
    description: 'Silver Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.005,
  },
  {
    symbol: 'HGZ25',
    assetClass: 'FUTURES',
    description: 'Copper Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0005,
  },

  // === BONDS (Treasury) ===
  {
    symbol: 'ZBZ25',
    assetClass: 'FUTURES',
    description: 'US Treasury Bond Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.03125,
  },
  {
    symbol: 'ZNZ25',
    assetClass: 'FUTURES',
    description: 'US Treasury Note 10Y Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.015625,
  },
  {
    symbol: 'ZFZ25',
    assetClass: 'FUTURES',
    description: 'US Treasury Note 5Y Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0078125,
  },
  {
    symbol: 'ZTZ25',
    assetClass: 'FUTURES',
    description: 'US Treasury Note 2Y Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.00390625,
  },

  // === FX FUTURES ===
  {
    symbol: '6AZ25',
    assetClass: 'FUTURES',
    description: 'Japanese Yen Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: 'M6AZ25',
    assetClass: 'FUTURES',
    description: 'Micro Japanese Yen Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: '6BZ25',
    assetClass: 'FUTURES',
    description: 'British Pound Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: 'M6BZ25',
    assetClass: 'FUTURES',
    description: 'Micro British Pound Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: '6CZ25',
    assetClass: 'FUTURES',
    description: 'Canadian Dollar Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: 'M6CZ25',
    assetClass: 'FUTURES',
    description: 'Micro Canadian Dollar Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: '6EZ25',
    assetClass: 'FUTURES',
    description: 'Euro Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.00005,
  },
  {
    symbol: 'M6EZ25',
    assetClass: 'FUTURES',
    description: 'Micro Euro Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: '6JZ25',
    assetClass: 'FUTURES',
    description: 'Japanese Yen (mini) Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0000005,
  },
  {
    symbol: 'M6JZ25',
    assetClass: 'FUTURES',
    description: 'Micro Japanese Yen (mini) Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.000001,
  },
  {
    symbol: '6NZ25',
    assetClass: 'FUTURES',
    description: 'Australian Dollar Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: 'M6NZ25',
    assetClass: 'FUTURES',
    description: 'Micro Australian Dollar Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: '6SZ25',
    assetClass: 'FUTURES',
    description: 'Swiss Franc Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },
  {
    symbol: 'M6SZ25',
    assetClass: 'FUTURES',
    description: 'Micro Swiss Franc Dec2025',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.0001,
  },

  // === UNSUPPORTED/UNKNOWN (should remain blank) ===
  {
    symbol: 'UNKNOWNZ25',
    assetClass: 'FUTURES',
    description: 'Hypothetical unknown futures root (intentionally unsupported)',
    expectedSource: 'unresolved' as const,
    expectedTickSize: null,
  },
  {
    symbol: 'AAPL',
    assetClass: 'CASH',
    description: 'Apple stock (not futures)',
    expectedSource: 'unresolved' as const,
    expectedTickSize: null,
  },
  {
    symbol: 'EUR/USD',
    assetClass: 'CASH',
    description: 'Forex spot (not futures)',
    expectedSource: 'unresolved' as const,
    expectedTickSize: null,
  },

  // === VENUE-PREFIXED SYMBOLS ===
  {
    symbol: 'CME:ESZ25',
    assetClass: 'FUTURES',
    description: 'S&P 500 E-mini with CME venue prefix',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.25,
  },
  {
    symbol: 'CME:CLF26',
    assetClass: 'FUTURES',
    description: 'Crude Oil with CME venue prefix',
    expectedSource: 'fallback-root-map' as const,
    expectedTickSize: 0.01,
  },
];

export interface ValidationResult {
  passed: number;
  failed: number;
  details: Array<{
    symbol: string;
    description: string;
    diagnostic: TickSizeResolutionDiagnostic;
    expectedSource: string;
    expectedTickSize: number | null;
    matches: boolean;
    error: string | null;
  }>;
}

/**
 * Validate a single symbol against its expected resolution.
 */
export function validateSymbol(symbol: string): TickSizeResolutionDiagnostic {
  const sample = VALIDATION_SAMPLE_SET.find(s => s.symbol === symbol);
  const diagnostic = getTickSizeResolutionDiagnostic(symbol, sample?.assetClass);
  return diagnostic;
}

/**
 * Validate all sample symbols and return a detailed report.
 */
export function validateAllSymbols(): ValidationResult {
  const result: ValidationResult = {
    passed: 0,
    failed: 0,
    details: [],
  };

  for (const sample of VALIDATION_SAMPLE_SET) {
    const diagnostic = getTickSizeResolutionDiagnostic(sample.symbol, sample.assetClass);
    const sourceMatches = diagnostic.resolutionSource === sample.expectedSource;
    const tickSizeMatches = diagnostic.resolvedTickSize === sample.expectedTickSize;
    const matches = sourceMatches && tickSizeMatches;

    if (matches) {
      result.passed++;
    } else {
      result.failed++;
    }

    result.details.push({
      symbol: sample.symbol,
      description: sample.description,
      diagnostic,
      expectedSource: sample.expectedSource,
      expectedTickSize: sample.expectedTickSize,
      matches,
      error: matches
        ? null
        : `source mismatch: ${diagnostic.resolutionSource} vs ${sample.expectedSource}` +
          ` | tick mismatch: ${diagnostic.resolvedTickSize} vs ${sample.expectedTickSize}`,
    });
  }

  return result;
}

/**
 * Print validation results to console in a readable format.
 */
export function printValidationReport(): void {
  const result = validateAllSymbols();
  console.group(`[TickSizeValidator] Validation Report`);
  console.log(`Passed: ${result.passed}/${VALIDATION_SAMPLE_SET.length}`);
  console.log(`Failed: ${result.failed}/${VALIDATION_SAMPLE_SET.length}`);

  if (result.failed > 0) {
    console.group('Failed Validations:');
    for (const detail of result.details) {
      if (!detail.matches) {
        console.error(
          `❌ ${detail.symbol} (${detail.description}): ${detail.error}`
        );
      }
    }
    console.groupEnd();
  }

  console.group('Detailed Results:');
  console.table(
    result.details.map(d => ({
      symbol: d.symbol,
      resolved: d.diagnostic.resolvedTickSize ?? 'null',
      source: d.diagnostic.resolutionSource,
      pass: d.matches ? '✓' : '✗',
    }))
  );
  console.groupEnd();
  console.groupEnd();
}

/**
 * Attach debug helpers to window object for easy console access.
 * Only available in development mode.
 */
export function attachDebugHelpers(): void {
  if (typeof window === 'undefined') return;

  const helpers = {
    validateSymbol,
    validateAllSymbols,
    printValidationReport,
    // Also expose the constants for reference
    CME_FUTURES_TICK_SIZES,
    extractFuturesRoot,
    getTickSizeResolutionDiagnostic,
  };

  (window as any).__tickSizeValidator = helpers;

  if (import.meta.env.DEV) {
    console.log(
      '[tickSizeValidator] Debug helpers attached to window.__tickSizeValidator'
    );
  }
}
